import { createClerkSupabaseClient } from '../lib/supabase.js';

// Función para verificar si ya existe un archivo con el mismo nombre
const checkExistingFile = async (supabase, fileName, clerkId, bucket = 'planes') => {
  try {
    console.log('🔍 fileUploadService: Verificando archivos existentes...', {
      fileName,
      clerkId,
      bucket
    });

    const { data, error } = await supabase
      .from('archivos_storage')
      .select('id, nombre_original, path, created_at')
      .eq('nombre_original', fileName)
      .eq('clerk_id', clerkId)
      .eq('bucket', bucket)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ fileUploadService: Error al verificar archivos existentes:', error);
      return { exists: false, files: [] };
    }

    console.log('📊 fileUploadService: Archivos existentes encontrados:', {
      count: data?.length || 0,
      files: data || []
    });

    return {
      exists: data && data.length > 0,
      files: data || []
    };
  } catch (error) {
    console.error('❌ fileUploadService: Error crítico al verificar archivos:', error);
    return { exists: false, files: [] };
  }
};

// Función para subir archivo a Supabase Storage
export const uploadFile = async (file, session, bucket = 'planes', folder = 'planes') => {
  try {
    console.log('🚀 fileUploadService: Iniciando proceso de subida...', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      bucket,
      folder,
      timestamp: new Date().toISOString()
    });

    // Crear cliente autenticado de Supabase
    const supabase = createClerkSupabaseClient(session);
    console.log('✅ fileUploadService: Cliente Supabase creado exitosamente');
    
    // Obtener el usuario desde la sesión de Clerk
    if (!session?.user?.id) {
      throw new Error('Usuario no autenticado');
    }

    console.log('👤 fileUploadService: Usuario autenticado:', {
      userId: session.user.id,
      userEmail: session.user.primaryEmailAddress?.emailAddress
    });

    // Validar que el archivo sea PDF
    if (file.type !== 'application/pdf') {
      throw new Error('Solo se permiten archivos PDF');
    }

    // Verificar archivos existentes
    const existingCheck = await checkExistingFile(supabase, file.name, session.user.id, bucket);
    
    if (existingCheck.exists && existingCheck.files.length > 0) {
      console.warn('⚠️ fileUploadService: Se encontraron archivos duplicados:', {
        count: existingCheck.files.length,
        existingFiles: existingCheck.files.map(f => ({
          id: f.id,
          path: f.path,
          created_at: f.created_at
        }))
      });

      // Por ahora, continuamos con la subida pero loggeamos la advertencia
      // En el futuro se podría implementar una confirmación al usuario
    }

    // Generar nombre único para el archivo
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${folder}/${fileName}`;

    console.log('📁 fileUploadService: Generando archivo con nombre único:', {
      originalName: file.name,
      uniqueName: fileName,
      fullPath: filePath
    });

    // Subir archivo a Storage
    console.log('⬆️ fileUploadService: Subiendo archivo a Supabase Storage...');
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file);

    if (uploadError) {
      console.error('❌ fileUploadService: Error al subir a Storage:', uploadError);
      throw uploadError;
    }

    console.log('✅ fileUploadService: Archivo subido exitosamente a Storage:', {
      path: uploadData.path,
      id: uploadData.id
    });

    // Obtener URL pública
    console.log('🔗 fileUploadService: Obteniendo URL pública...');
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    console.log('✅ fileUploadService: URL pública generada:', urlData.publicUrl);

    // Crear registro en archivos_storage
    console.log('💾 fileUploadService: Creando registro en base de datos...');
    const { data: archivoData, error: archivoError } = await supabase
      .from('archivos_storage')
      .insert({
        bucket: bucket,
        path: filePath,
        url: urlData.publicUrl,
        tipo: file.type,
        nombre_original: file.name,
        creado_por: null,
        clerk_id: session.user.id  // Nuevo campo para el ID de Clerk
      })
      .select()
      .single();

    if (archivoError) {
      console.error('❌ fileUploadService: Error al crear registro en BD:', archivoError);
      
      // Si falla la inserción en BD, intentar limpiar el archivo del storage
      try {
        console.log('🧹 fileUploadService: Limpiando archivo del storage debido a error en BD...');
        await supabase.storage.from(bucket).remove([filePath]);
        console.log('✅ fileUploadService: Archivo limpiado del storage');
      } catch (cleanupError) {
        console.error('❌ fileUploadService: Error al limpiar archivo del storage:', cleanupError);
      }
      
      throw archivoError;
    }

    console.log('✅ fileUploadService: Registro creado exitosamente en BD:', {
      id: archivoData.id,
      path: archivoData.path,
      url: archivoData.url
    });

    console.log('🎉 fileUploadService: Proceso de subida completado exitosamente');

    return {
      success: true,
      archivo: archivoData,
      url: urlData.publicUrl
    };
  } catch (error) {
    console.error('❌ fileUploadService: Error crítico en uploadFile:', {
      error: error.message,
      stack: error.stack,
      fileName: file?.name,
      timestamp: new Date().toISOString()
    });
    
    return {
      success: false,
      error: error.message
    };
  }
};

// Función para obtener información del archivo
export const getFileInfo = async (archivoId, session) => {
  try {
    if (!archivoId || !session) {
      return { success: false, error: 'ID de archivo o sesión no disponible' };
    }

    // Si es una URL, no necesita buscar en BD
    if (typeof archivoId === 'string' && archivoId.startsWith('http')) {
      return {
        success: true,
        archivo: {
          id: archivoId,
          url: archivoId,
          nombre_original: 'Archivo PDF',
          isDirectUrl: true
        }
      };
    }

    const supabase = createClerkSupabaseClient(session);

    const { data: archivo, error } = await supabase
      .from('archivos_storage')
      .select('id, nombre_original, url, bucket, path, created_at, tipo')
      .eq('id', archivoId)
      .maybeSingle();

    if (error) {
      console.warn('Advertencia al obtener información del archivo:', error.message);
      return {
        success: false,
        error: 'No se pudo obtener la información del archivo',
        fileId: archivoId
      };
    }

    if (!archivo) {
      return {
        success: false,
        error: 'Archivo no encontrado en la base de datos',
        fileId: archivoId
      };
    }

    // Si tenemos el archivo, reconstruir la URL desde el path si es necesario
    let fileUrl = archivo.url;

    // Si la URL está rota o no es válida, reconstruirla desde el path
    if (archivo.path && archivo.bucket) {
      const { data: urlData } = supabase.storage
        .from(archivo.bucket)
        .getPublicUrl(archivo.path);

      if (urlData?.publicUrl) {
        fileUrl = urlData.publicUrl;
      }
    }

    return {
      success: true,
      archivo: {
        ...archivo,
        url: fileUrl
      }
    };
  } catch (error) {
    console.warn('Error en getFileInfo:', error.message);
    return {
      success: false,
      error: error.message,
      fileId: archivoId
    };
  }
};

// Función para eliminar archivo - Versión original restaurada
export const deleteFile = async (archivoId, session) => {
  try {
    console.log('🗑️ fileUploadService: Iniciando proceso de eliminación...', {
      archivoId,
      timestamp: new Date().toISOString()
    });

    // Crear cliente autenticado de Supabase
    const supabase = createClerkSupabaseClient(session);
    console.log('✅ fileUploadService: Cliente Supabase creado exitosamente');
    
    // Verificar autenticación
    if (!session?.user?.id) {
      throw new Error('Usuario no autenticado');
    }

    console.log('👤 fileUploadService: Usuario autenticado:', {
      userId: session.user.id
    });

    // PASO 1: Obtener información del archivo
    console.log('🔍 fileUploadService: Obteniendo información del archivo...');
    const { data: archivo, error: fetchError } = await supabase
      .from('archivos_storage')
      .select('*')
      .eq('id', archivoId)
      .single();

    if (fetchError) {
      console.error('❌ fileUploadService: Error al obtener archivo:', fetchError);
      throw fetchError;
    }

    if (!archivo) {
      throw new Error('Archivo no encontrado');
    }

    console.log('📄 fileUploadService: Archivo encontrado:', {
      id: archivo.id,
      nombre: archivo.nombre_original,
      bucket: archivo.bucket,
      path: archivo.path
    });

    // PASO 2: Eliminar registro de la base de datos
    // Las foreign key constraints con ON DELETE SET NULL manejarán automáticamente las referencias
    console.log('🗑️ fileUploadService: Eliminando registro de la base de datos...');
    const { error: deleteError } = await supabase
      .from('archivos_storage')
      .delete()
      .eq('id', archivoId);

    if (deleteError) {
      console.error('❌ fileUploadService: Error al eliminar de BD:', deleteError);
      throw deleteError;
    }

    console.log('✅ fileUploadService: Registro eliminado de la base de datos');

    // PASO 3: Eliminar archivo del Storage
    console.log('🗑️ fileUploadService: Eliminando archivo del Storage...');
    
    // Usar el método de administrador para evitar problemas con owner_id/clerk_id
    const { error: storageError } = await supabase.storage
      .from(archivo.bucket)
      .remove([archivo.path]);

    if (storageError) {
      console.warn('⚠️ fileUploadService: Error al eliminar del storage:', {
        error: storageError,
        bucket: archivo.bucket,
        path: archivo.path
      });
      
      // Intentar eliminación alternativa si el primer método falla
      try {
        console.log('🔄 fileUploadService: Intentando eliminación alternativa...');
        const { error: altError } = await supabase.storage
          .from(archivo.bucket)
          .remove([archivo.path], { 
            upsert: false 
          });
          
        if (altError) {
          console.warn('⚠️ fileUploadService: Eliminación alternativa también falló:', altError);
        } else {
          console.log('✅ fileUploadService: Eliminación alternativa exitosa');
        }
      } catch (altException) {
        console.warn('⚠️ fileUploadService: Excepción en eliminación alternativa:', altException);
      }
      
      // No lanzamos error aquí porque el registro ya fue eliminado de la BD
    } else {
      console.log('✅ fileUploadService: Archivo eliminado del Storage exitosamente');
    }

    console.log('🎉 fileUploadService: Eliminación completada exitosamente');
    return { 
      success: true,
      message: 'Archivo eliminado exitosamente'
    };

  } catch (error) {
    console.error('❌ fileUploadService: Error en deleteFile:', {
      error: error.message,
      archivoId,
      userId: session?.user?.id,
      timestamp: new Date().toISOString()
    });
    
    return {
      success: false,
      error: error.message
    };
  }
};
